import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  api,
  type OrgWithStats,
  type MspDashboardStats,
  type MSP,
} from '../api/client'
import {
  StatusIndicator,
  NetworkQualityIndicator,
  StatBadge,
  SummaryCard,
} from '../components/StatusIndicator'

// Client organization card
function ClientCard({ org, onClick }: { org: OrgWithStats, onClick: () => void }) {
  const accentColors = {
    healthy: 'border-emerald-500/30 hover:border-emerald-500/50',
    warning: 'border-amber-500/30 hover:border-amber-500/50',
    critical: 'border-red-500/30 hover:border-red-500/50',
  }

  return (
    <button
      onClick={onClick}
      className={`
        group relative w-full text-left
        bg-slate-900/50 backdrop-blur-sm
        border ${accentColors[org.overallHealth]}
        rounded-xl p-5
        transition-all duration-300 ease-out
        hover:bg-slate-800/50 hover:translate-y-[-2px]
        hover:shadow-lg hover:shadow-black/20
        focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 focus:ring-offset-slate-950
      `}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white tracking-tight">
              {org.name}
            </h3>
            <p className="text-slate-500 text-sm font-mono mt-0.5">
              {org.stats.total} device{org.stats.total !== 1 ? 's' : ''}
            </p>
          </div>
          {/* Dual indicator: Device status + Network quality */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center gap-1" title="Device Status">
              <StatusIndicator status={org.overallHealth} size="lg" pulse={org.overallHealth === 'critical'} />
              <span className="text-[10px] text-slate-600">Device</span>
            </div>
            <div className="flex flex-col items-center gap-1" title="Network Quality">
              <NetworkQualityIndicator status={org.stats.networkQuality.overall} />
              <span className="text-[10px] text-slate-600">Network</span>
            </div>
          </div>
        </div>

        {/* Device status row */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 mb-3">
          <StatBadge count={org.stats.healthy} status="healthy" label="healthy" />
          <StatBadge count={org.stats.warning} status="warning" label="warning" />
          <StatBadge count={org.stats.critical} status="critical" label="critical" />
        </div>

        {/* Location and network row */}
        <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
          {/* Location breakdown */}
          <div className="flex items-center gap-2">
            {org.stats.locations.office > 0 && (
              <span className="flex items-center gap-1 text-blue-400" title="Office">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                </svg>
                <span className="font-mono">{org.stats.locations.office}</span>
              </span>
            )}
            {org.stats.locations.home > 0 && (
              <span className="flex items-center gap-1 text-purple-400" title="Home">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                </svg>
                <span className="font-mono">{org.stats.locations.home}</span>
              </span>
            )}
            {org.stats.locations.remote > 0 && (
              <span className="flex items-center gap-1 text-amber-400" title="Remote">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                </svg>
                <span className="font-mono">{org.stats.locations.remote}</span>
              </span>
            )}
          </div>

          {/* Network quality breakdown */}
          <div className="flex items-center gap-2">
            {org.stats.networkQuality.good > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="font-mono">{org.stats.networkQuality.good}</span>
              </span>
            )}
            {org.stats.networkQuality.degraded > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                <span className="font-mono">{org.stats.networkQuality.degraded}</span>
              </span>
            )}
            {org.stats.networkQuality.poor > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                <span className="font-mono">{org.stats.networkQuality.poor}</span>
              </span>
            )}
            {org.stats.networkQuality.offline > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                <span className="font-mono">{org.stats.networkQuality.offline}</span>
              </span>
            )}
          </div>
        </div>

        {/* Footer metrics */}
        <div className="mt-4 pt-3 border-t border-slate-700/50 flex items-center justify-between text-xs">
          <div className="flex items-center gap-3">
            <span className="text-slate-500">
              <span className="text-emerald-400 font-mono">{org.stats.connectionStates.streaming}</span> streaming
            </span>
            <span className="text-slate-500">
              <span className="text-amber-400 font-mono">{org.stats.connectionStates.idle}</span> idle
            </span>
          </div>
          <span className="text-slate-500">
            <span className="text-slate-400 font-mono">{org.stats.currentVersion}</span>/{org.stats.total} current
          </span>
        </div>
      </div>
    </button>
  )
}

// Add Client section with inline form
function AddClientSection({
  onCreated,
}: {
  onCreated: (org: { orgId: string; name: string; mspId: string; slug: string }) => void
}) {
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || name.trim().length < 2) {
      setError('Name must be at least 2 characters')
      return
    }
    setCreating(true)
    setError(null)
    try {
      const result = await api.createOrganization(name.trim())
      onCreated(result.data)
      setSuccess(`Created "${result.data.name}"`)
      setName('')
      setTimeout(() => {
        setShowForm(false)
        setSuccess(null)
      }, 2000)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create organization')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="mb-4">
      {!showForm ? (
        <button
          onClick={() => { setShowForm(true); setError(null); setSuccess(null) }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 text-slate-300 hover:text-white rounded-lg transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Client
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="flex items-center gap-3 bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Organization name (e.g. Acme Corp)"
            autoFocus
            className="flex-1 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500/50"
            minLength={2}
            maxLength={100}
          />
          <button
            type="submit"
            disabled={creating || name.trim().length < 2}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 text-slate-900 font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
          <button
            type="button"
            onClick={() => { setShowForm(false); setName(''); setError(null) }}
            className="px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors text-sm"
          >
            Cancel
          </button>
          {error && <span className="text-red-400 text-sm">{error}</span>}
          {success && <span className="text-emerald-400 text-sm">{success}</span>}
        </form>
      )}
    </div>
  )
}

export default function OrgsPage({
  organizations,
  setOrganizations,
  dashboardStats,
  setDashboardStats,
}: {
  organizations: OrgWithStats[]
  setOrganizations: React.Dispatch<React.SetStateAction<OrgWithStats[]>>
  dashboardStats: MspDashboardStats
  setDashboardStats: React.Dispatch<React.SetStateAction<MspDashboardStats | null>>
  msp: MSP | null
}) {
  const navigate = useNavigate()

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white tracking-tight">Client Overview</h1>
        <p className="text-slate-500 mt-1">
          Managing {dashboardStats.totalDevices} devices across {dashboardStats.totalOrgs} clients
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <SummaryCard
          label="Total Devices"
          value={dashboardStats.totalDevices}
          icon="devices"
        />
        <SummaryCard
          label="Healthy"
          value={dashboardStats.healthy}
          icon="check"
          color="emerald"
        />
        <SummaryCard
          label="Warnings"
          value={dashboardStats.warning}
          icon="warning"
          color="amber"
        />
        <SummaryCard
          label="Critical"
          value={dashboardStats.critical}
          icon="alert"
          color="red"
        />
      </div>

      {/* Add client + client cards grid */}
      <AddClientSection
        onCreated={(org) => {
          setOrganizations(prev => [...prev, {
            ...org,
            stats: {
              orgId: org.orgId,
              total: 0, healthy: 0, warning: 0, critical: 0,
              onlineNow: 0, currentVersion: 0,
              networkQuality: { good: 0, degraded: 0, poor: 0, offline: 0, overall: 'offline' as const },
              locations: { office: 0, home: 0, remote: 0, unknown: 0 },
              connectionStates: { streaming: 0, idle: 0, offline: 0 },
            },
            overallHealth: 'healthy' as const,
          }])
          if (dashboardStats) {
            setDashboardStats({ ...dashboardStats, totalOrgs: dashboardStats.totalOrgs + 1 })
          }
        }}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {organizations.map(org => (
          <ClientCard
            key={org.orgId}
            org={org}
            onClick={() => navigate(`/orgs/${org.orgId}`)}
          />
        ))}
      </div>
    </>
  )
}
